// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
// Material
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';

// RxJS
import { tap } from 'rxjs/operators';
import {
  PatientHeightModel,
  PatientHeightOnServerCreated,
  PatientHeightsDataSource,
  PatientHeightsPageRequested,
  selectPatientHeightErrorState,
  selectPatientHeightsActionLoading,
} from 'src/app/core/lists';
import { merge, Observable, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
  TypesUtilsService,
} from '../../../../../../../core/_base/crud';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../core/reducers';
import { DatePipe } from '@angular/common';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM-DD-YY',
  },
  display: {
    dateInput: 'MM-DD-YY',
    monthYearLabel: 'MMM, YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'kt-patient-height',
  templateUrl: './patient-height.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class PatientHeightComponent implements OnInit {
  // Public properties
  // Incoming data
  @Input() patientId$: Observable<number>;
  patientId: number;
  // Table fields
  dataSource: PatientHeightsDataSource;
  displayedColumns = ['height', 'start_date'];
  displayedColumnsForAdd = ['add-height', 'add-startDate'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  // Filter fields
  //@ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  filterStatus = '';
  filterHoldHospital = '';
  filterType = '';
  isSwitchedToEditMode = false;
  loadingAfterSubmit = false;
  formGroup: FormGroup;
  heightForEdit: PatientHeightModel;
  heightForAdd: PatientHeightModel;
  submissionErrorObj: any;
  hasSubmissionErrors = false;
  // Selection
  selection = new SelectionModel<PatientHeightModel>(true, []);
  patientHeightsResult: PatientHeightModel[] = [];
  // Subscriptions
  private subscriptions: any[] = [];
  private componentSubscriptions: Subscription;
  measurementSystem = localStorage.getItem('measurementSystem');

  /**
   * Component constructor
   *
   * @param dialogRef
   * @param data
   * @param snackBar
   * @param layoutUtilsService
   * @param translate
   * @param store
   * @param cdr
   * @param fb
   * @param typesUtilsService
   * @param datePipe
   */

  constructor(
    public dialogRef: MatDialogRef<PatientHeightComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    public typesUtilsService: TypesUtilsService,
    private datePipe: DatePipe,
  ) {
    this.store.subscribe();
    this.heightForAdd = new PatientHeightModel();
  }

  /**
   * On init
   */
  ngOnInit(): void {
    this.displayedColumns =
      this.measurementSystem === 'm'
        ? ['height', 'start_date']
        : ['height_ft', 'height_in', 'start_date'];
    this.displayedColumnsForAdd =
      this.measurementSystem === 'm'
        ? ['add-height', 'add-startDate', 'add-actions']
        : ['add-height_ft', 'add-height_in', 'add-startDate', 'add-actions'];
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    /* Data load will be triggered in two cases:
		- when a pagination event occurs => this.paginator.page
		- when a sort event occurs => this.sort.sortChange
		**/
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadPatientHeightsList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });
    // Init DataSource
    this.dataSource = new PatientHeightsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject.subscribe(
      (res) => (this.patientHeightsResult = res),
    );
    this.subscriptions.push({ id: 3, sub: entitiesSubscription });

    this.patientId = this.data.id;
    this.loadPatientHeightsList();
    this.createFormGroup();
  }

  /**
   * Load Patients List from service through data-sources
   */
  loadPatientHeightsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(new PatientHeightsPageRequested({ page: queryParams, id: this.data.id }));
    this.selection.clear();
  }

  createFormGroup() {
    this.formGroup =
      this.measurementSystem === 'm'
        ? this.fb.group({
            editHeight: ['', Validators.compose([Validators.required])],
            editStartDate: [
              this.typesUtilsService.getDateFromString(),
              Validators.compose([Validators.required]),
            ],
            newHeight: ['', Validators.compose([Validators.required])],
            newStartDate: [
              this.typesUtilsService.getDateFromString(),
              Validators.compose([Validators.required]),
            ],
          })
        : this.fb.group({
            editHeightFt: ['', Validators.compose([Validators.required])],
            editHeightIn: ['', Validators.compose([Validators.required])],
            editStartDate: [
              this.typesUtilsService.getDateFromString(),
              Validators.compose([Validators.required]),
            ],
            newHeightFt: ['', Validators.compose([Validators.required])],
            newHeightIn: ['', Validators.compose([Validators.required])],
            newStartDate: [
              this.typesUtilsService.getDateFromString(),
              Validators.compose([Validators.required]),
            ],
          });
    this.clearAddForm();
    this.clearEditForm();
  }

  // ADD Height FUNCTIONS: clearAddForm | checkAddForm | addHeightButtonOnClick | cancelAddButtonOnClick | saveNewHeight
  clearAddForm() {
    const controls = this.formGroup.controls;
    if (this.measurementSystem === 'm') {
      controls.newHeight.setValue('');
      controls.newHeight.markAsPristine();
      controls.newHeight.markAsUntouched();
      controls.newStartDate.setValue(this.typesUtilsService.getDateFromString());
      controls.newStartDate.markAsPristine();
      controls.newStartDate.markAsUntouched();
    } else {
      controls.newHeightFt.setValue('');
      controls.newHeightFt.markAsPristine();
      controls.newHeightFt.markAsUntouched();
      controls.newHeightIn.setValue('');
      controls.newHeightIn.markAsPristine();
      controls.newHeightIn.markAsUntouched();
      controls.newStartDate.setValue(this.typesUtilsService.getDateFromString());
      controls.newStartDate.markAsPristine();
      controls.newStartDate.markAsUntouched();
    }
    this.heightForAdd = new PatientHeightModel();
    this.heightForAdd.clear(this.patientId);
    this.heightForAdd.start_date = this.typesUtilsService.getDateStringFromDate();
    this.heightForAdd._isEditMode = false;
  }

  /**
   * Check if Add Form is Valid
   */
  checkAddForm() {
    const controls = this.formGroup.controls;
    if (this.measurementSystem === 'm') {
      if (controls.newHeight.invalid || controls.newStartDate.invalid) {
        controls.newHeight.markAsTouched();
        controls.newStartDate.markAsTouched();
        return false;
      }
    } else {
      if (
        controls.newHeightFt.invalid ||
        controls.newHeightIn.invalid ||
        controls.newStartDate.invalid
      ) {
        controls.newHeightFt.markAsTouched();
        controls.newHeightIn.markAsTouched();
        controls.newStartDate.markAsTouched();
        return false;
      }
    }

    return true;
  }

  /**
   * Close Remark Add Form
   */
  cancelAddButtonOnClick() {
    this.heightForAdd._isEditMode = false;
    this.isSwitchedToEditMode = false;
  }

  /**
   *  Create new Height
   */
  saveNewHeight() {
    if (!this.checkAddForm()) {
      return;
    }

    const controls = this.formGroup.controls;
    this.loadingAfterSubmit = false;
    this.heightForAdd._isEditMode = false;
    this.heightForAdd.height =
      this.measurementSystem === 'm'
        ? controls.newHeight.value
        : controls.newHeightFt.value * 30.48 + controls.newHeightIn.value * 2.54;
    const _date = new Date(controls.newStartDate.value);
    this.heightForAdd.start_date = this.datePipe.transform(_date, 'yyyy-MM-dd');
    this.heightForAdd._createdDate = this.heightForEdit._updatedDate;
    this.store.dispatch(
      new PatientHeightOnServerCreated({ patientHeight: this.heightForAdd, id: this.patientId }),
    );

    const actionLoadingSubscription = this.store
      .pipe(select(selectPatientHeightsActionLoading))
      .subscribe((res) => {
        if (res !== true) {
          const errorStateSubscription = this.store
            .pipe(select(selectPatientHeightErrorState))
            .subscribe((error) => {
              if (error) {
                if (error.status) {
                  this.submissionErrorObj = error.error.errors;
                } else {
                  this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                }
                this.hasSubmissionErrors = true;
              } else {
                this.submissionErrorObj = null;
                this.hasSubmissionErrors = false;
                const message = this.translate.instant('PATIENT.PATIENT_HEIGHT.EDIT.ADD_MESSAGE');
                this.layoutUtilsService.showActionNotification(
                  message,
                  MessageType.Update,
                  10000,
                  true,
                  true,
                );
                this.isSwitchedToEditMode = false;
                this.clearAddForm();
              }
              this.cdr.detectChanges();
            });
          this.subscriptions.push({ id: 5, sub: errorStateSubscription });
        }
        this.cdr.detectChanges();
      });
    this.subscriptions.push({ id: 4, sub: actionLoadingSubscription });
  }

  // EDIT Height FUNCTIONS: clearEditForm | checkEditForm | editHeightButtonOnClick | cancelEditButtonOnClick |
  clearEditForm() {
    const controls = this.formGroup.controls;
    if (this.measurementSystem === 'm') {
      controls.editHeight.setValue('');
      controls.editStartDate.setValue(this.typesUtilsService.getDateFromString());
    } else {
      controls.editHeightFt.setValue('');
      controls.editHeightIn.setValue('');
      controls.editStartDate.setValue(this.typesUtilsService.getDateFromString());
    }

    this.heightForEdit = new PatientHeightModel();
    this.heightForEdit.clear(this.patientId);
    this.heightForEdit.start_date = this.typesUtilsService.getDateStringFromDate();
    this.heightForEdit._isEditMode = false;
  }

  /**
   * Open Height Add Form
   */
  addHeightButtonOnClick() {
    this.clearAddForm();
    this.heightForAdd._isEditMode = true;
    this.isSwitchedToEditMode = true;
    this.cdr.detectChanges();
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    // const searchText: string = this.searchInput.nativeElement.value;
    filter.status = this.filterStatus;
    //filter.text = searchText ? searchText : '';

    // if (!searchText) {
    //   return filter;
    // }

    // filter.name = searchText;
    // filter.patient = searchText;
    return filter;
  }

  convertHeightFt(height) {
    return Math.floor(height / 2.54 / 12);
  }
  convertHeightIn(height) {
    return Math.round(height / 2.54 - 12 * Math.floor(height / 2.54 / 12));
  }

  /**
   * Close dialog
   */
  closeDialog(): void {
    const sortedResult = this.patientHeightsResult.sort(function (a, b) {
      const x = a['start_date'];
      const y = b['start_date'];
      return x < y ? -1 : x > y ? 1 : 0;
    });
    this.dialogRef.close({
      height: sortedResult.length > 0 ? sortedResult[sortedResult.length - 1].height : 0,
    });
  }
}
